import DocumentPage from './DocumentPage';

export const hrDocument = {
  slug: 'hr',
  route: '/hr',
  title: 'Human Resources',
  category: 'People & Operations',
  format: 'DOCX',
  description:
    'The Human Resource module in EduX ERP manages employees, resume history, work and private information, contracts, departments, salary attachments, and HR configuration masters.',
  metrics: [
    { label: 'Primary focus', value: 'Employee administration' },
    { label: 'Core records', value: 'Employees and contracts' },
    { label: 'Structure', value: 'Departments and hierarchy' },
  ],
  workflow: [
    'Create and maintain employee records with resume, work, private, and HR settings information.',
    'Manage contracts, departments, and salary attachments for employee administration and payroll integration.',
    'Configure HR master data such as work locations, skills, tags, job positions, employment types, schedules, and on/offboarding plans.',
  ],
  sections: [
    {
      id: 'hr-overview',
      title: 'Human Resource Module Documentation',
      body: [
        'The Human Resource Module in EduX ERP is used to manage employee information, organizational structure, employee skills, and department management.',
        'It helps HR administrators maintain records of employees, their departments, reporting hierarchy, and skill details.',
        'The HR module ensures proper management of staff and faculty within the organization.',
      ],
    },
    {
      id: 'employees',
      title: 'Employees',
      body: [
        'Go to HR -> Employees -> Employees. The Employees menu is used to store and manage detailed information about employees working in the organization.',
        'HR administrators can create new employee records, update employee details, and manage employment information.',
        'This section includes personal details, job position, department, contact information, and other HR-related data.',
      ],
      figure: {
        src: '/assets/doc-images/hr/image.png',
        alt: 'Employee form in Human Resources module',
        caption: 'Employee record showing name, job title, department, contact details, and the main HR tabs.',
      },
      subsections: [
        {
          id: 'resume-line',
          title: 'Resume Tab and Resume Line',
          body: [
            'Go to HR -> Employees -> Employees -> Open Employee -> Resume Tab -> Add Resume Line.',
            'The Resume Line section is used to record the education background, work experience, certifications, or achievements of an employee.',
            'This helps the HR department maintain a complete professional history of employees within the system.',
            'Each resume entry represents a specific qualification or experience of the employee.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image2.png',
            alt: 'Create resume line modal',
            caption: 'Resume Line form used to record education, experience, certifications, or achievements.',
          },
        },
        {
          id: 'work-information',
          title: 'Work Information',
          body: [
            'Go to HR -> Employees -> Employees -> Open Employee -> Work Information.',
            'The Work Information section contains details related to the employee job location, company assignment, work schedule, and approval authorities.',
            'This information helps the organization manage employee work settings, attendance, expenses, and leave approvals.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image3.png',
            alt: 'Employee work information tab',
            caption: 'Work Information tab showing work address, work location, approvers, schedule, and organization chart.',
          },
        },
        {
          id: 'private-information',
          title: 'Private Information',
          body: [
            'Go to HR -> Employees -> Employees -> Open Employee -> Private Information.',
            'The Private Information section stores confidential personal details such as contact information, family details, education background, identification numbers, and work permit information.',
            'This information is generally accessible only to HR managers and authorized personnel and is used for administrative and legal purposes.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/hr/image4.png',
              alt: 'Employee private information tab with private contact and family status',
              caption: 'Private Information tab showing private contact details, family status, emergency, and education fields.',
            },
            {
              src: '/assets/doc-images/hr/image5.png',
              alt: 'Employee private information tab with citizenship and work permit',
              caption: 'Private Information tab showing citizenship and work permit fields.',
            },
          ],
          bullets: [
            'Private Contact Information stores the personal contact details of the employee.',
            'Family, education, identification, and work-permit data are maintained here for authorized HR use.',
          ],
        },
        {
          id: 'hr-settings',
          title: 'HR Settings',
          body: [
            'Go to HR -> Employees -> Employees -> Open Employee -> HR Settings.',
            'The HR Settings section contains administrative and system-related configurations for an employee.',
            'These settings are mainly used for system access, attendance tracking, identification, and integration with other modules such as Point of Sale, Attendance, and Fleet.',
            'This section helps the HR department manage employee status and system-related settings.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image6.png',
            alt: 'Employee HR settings tab',
            caption: 'HR Settings tab showing employee type, related user, PIN code, badge ID, and application settings.',
          },
        },
      ],
    },
    {
      id: 'contracts',
      title: 'Contract',
      body: [
        'Go to HR -> Employees -> Contracts.',
        'The Employee Contract is used to define the employment agreement between the organization and the employee.',
        'It contains important information such as salary structure, working schedule, contract duration, allowances, and deductions.',
        'The contract ensures that employee compensation and employment terms are clearly defined and can be integrated with payroll processing.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/hr/image7.png',
          alt: 'Employee contract main details',
          caption: 'Contract form showing employee, department, salary structure type, working schedule, dates, and wage details.',
        },
        {
          src: '/assets/doc-images/hr/image8.png',
          alt: 'Employee contract allowances and deductions',
          caption: 'Contract salary details showing allowances and deductions.',
        },
      ],
      detailCards: [
        {
          title: 'Common statuses',
          bullets: [
            'New: contract has been created but is not yet active.',
            'Running: contract is currently active.',
            'Expired: contract duration has ended.',
            'Closed: contract has been manually closed.',
          ],
        },
      ],
    },
    {
      id: 'departments',
      title: 'Departments',
      body: [
        'Go to HR -> Departments.',
        'The Departments menu is used to create and manage different departments within the organization.',
        'Departments help organize employees into various functional units such as Academic, Administration, Finance, and Human Resources.',
        'Each department can have a manager and a parent department, which helps define the organizational structure.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/hr/image9.png',
          alt: 'Department form with manager and parent department',
          caption: 'Department record showing manager, parent department, and department organization tree.',
        },
        {
          src: '/assets/doc-images/hr/imagea.png',
          alt: 'Department list view',
          caption: 'Departments list used to manage organizational units and hierarchy.',
        },
      ],
      subsections: [
        {
          id: 'config-departments',
          title: 'Configuration Departments',
          body: [
            'Go to HR -> Configuration -> Departments or HR -> Configuration -> Employee -> Departments.',
            'The Departments option is used to create and manage departments within the organization.',
            'Departments help organize employees based on their roles and responsibilities. Each department can have a manager and multiple employees working under it.',
            'Departments also help in maintaining a clear organizational structure and reporting hierarchy within the company.',
          ],
        },
      ],
    },
    {
      id: 'salary-attachments',
      title: 'Salary Attachments',
      body: [
        'Go to HR -> Employees -> Salary Attachments.',
        'The Salary Attachments feature is used to record deductions or payments that must be attached to an employee salary.',
        'These attachments are typically related to legal obligations, loans, or financial adjustments that need to be processed through payroll.',
        'Salary attachments ensure that specific amounts are automatically deducted or added during payslip generation.',
      ],
      figure: {
        src: '/assets/doc-images/hr/imageb.png',
        alt: 'Salary attachment record',
        caption: 'Salary Attachment record showing employee, dates, amount, and lifecycle status.',
      },
      detailCards: [
        {
          title: 'Salary Attachment Workflow',
          bullets: [
            'New: initial stage when a salary attachment record is created but not yet active.',
            'Running: after Set Running is clicked, the attachment becomes active and starts applying to pay slips.',
            'Expired: status shown when the end date is reached.',
            'Cancelled: the salary attachment is cancelled and no longer affects salary.',
          ],
        },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The HR Configuration area defines employee-related master data used across the organization.',
      ],
      subsections: [
        {
          id: 'work-locations',
          title: 'Work Locations',
          body: [
            'Go to HR -> Configuration -> Employee -> Work Location.',
            'Work Locations are used to define the different office locations or campuses where employees work.',
            'This helps organizations manage employees working in different branches.',
          ],
          figure: {
            src: '/assets/doc-images/hr/imagec.png',
            alt: 'Work locations list',
            caption: 'Work Locations list used to manage office and campus locations.',
          },
        },
        {
          id: 'departure-reasons',
          title: 'Departure Reasons',
          body: [
            'Go to HR -> Configuration -> Employee -> Departure Reasons.',
            'The Departure Reasons menu is used to define reasons for employee resignation or termination.',
            'This helps HR track why employees leave the organization.',
          ],
          figure: {
            src: '/assets/doc-images/hr/imaged.png',
            alt: 'Departure reasons list',
            caption: 'Departure Reasons list used to classify resignations and exits.',
          },
        },
        {
          id: 'skill-types',
          title: 'Skill Types',
          body: [
            'Go to HR -> Configuration -> Employee -> Skill Types.',
            'The Skill Types option is used to define categories of employee skills. These categories help HR organize different skills possessed by employees.',
          ],
          figure: {
            src: '/assets/doc-images/hr/imagee.png',
            alt: 'Skill types list',
            caption: 'Skill Types used to organize employee capabilities.',
          },
        },
        {
          id: 'tags',
          title: 'Tags',
          body: [
            'Go to HR -> Configuration -> Employee -> Tags.',
            'The Tags option is used to categorize employees based on specific characteristics. Tags make it easier to filter and search for employee records.',
          ],
          figure: {
            src: '/assets/doc-images/hr/imagef.png',
            alt: 'Employee tags list',
            caption: 'Employee Tags used for filtering and employee categorization.',
          },
        },
        {
          id: 'line-types',
          title: 'Line Types',
          body: [
            'Go to HR -> Configuration -> Resume -> Line Types.',
            'The Line Types menu is used to define categories for employee resume entries such as education, work experience, certifications, or achievements.',
            'This helps structure employee resume information.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image10.png',
            alt: 'Resume line types list',
            caption: 'Resume Line Types used to structure education and experience records.',
          },
        },
        {
          id: 'job-positions',
          title: 'Job Positions',
          body: [
            'Go to HR -> Configuration -> Recruitment -> Job Positions.',
            'The Job Positions menu is used to create and manage job roles available in the organization.',
            'These positions can later be assigned to employees.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image11.png',
            alt: 'Job positions list',
            caption: 'Job Positions list showing available roles and department mapping.',
          },
        },
        {
          id: 'employment-types',
          title: 'Employment Types',
          body: [
            'Go to HR -> Configuration -> Recruitment -> Employment Types.',
            'The Employment Types option defines different types of employment categories for employees.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image12.png',
            alt: 'Employment types list',
            caption: 'Employment Types list showing permanent, temporary, seasonal, and other categories.',
          },
        },
        {
          id: 'working-schedule-name',
          title: 'Working Schedule Name',
          body: [
            'Go to HR -> Configuration -> Contract -> Working Schedules.',
            'This field is used to enter the name of the working schedule.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image13.png',
            alt: 'Working schedules list',
            caption: 'Working Schedules list used for employee contract scheduling.',
          },
        },
        {
          id: 'on-offboarding-plans',
          title: 'On/Offboarding Plans',
          body: [
            'Go to HR -> Configuration -> Activity Planning -> On/Offboarding Plans.',
            'The On/Offboarding Plans feature is used to manage the tasks that must be completed when an employee joins the organization or leaves the organization.',
            'These plans help HR ensure that required steps such as document verification, system access, and asset handover are properly handled.',
          ],
          figure: {
            src: '/assets/doc-images/hr/image14.png',
            alt: 'Onboarding and offboarding plans list',
            caption: 'On/Offboarding Plans list used to manage structured joining and exit activities.',
          },
        },
      ],
    },
  ],
  related: ['payroll', 'time-off', 'faculty'],
};

export default function HRPage() {
  return <DocumentPage document={hrDocument} />;
}
